import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Route, Switch, useLocation} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'

import PrimeReact from 'primereact/api'
import {Tooltip} from 'primereact/tooltip'

//#region Global StyleSheet
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css'
import 'leaflet/dist/leaflet.css'
import 'primeflex/primeflex.css'
import 'primeicons/primeicons.css'
import 'primereact/resources/primereact.css'
import 'prismjs/themes/prism-coy.css'
import 'react-phone-input-2/lib/style.css'
import './App.scss'
import './assets/components/Components.scss'
import './assets/layout/layout.scss'
import './custom.css'

//#endregion

//#region Context Providers
//#endregion

//#region Import Page Components

import {useSignals} from '@preact/signals-react/runtime'
import classNames from 'classnames'
import {useIdleTimer} from 'react-idle-timer'
import {AppMenu} from './AppMenu'
import {AppTopbar} from './AppTopbar'
import {SystemRole} from './Constants'
import {ReactComponent as CalendarIcon} from './assets/icons/CalendarIcon.svg'
import {ReactComponent as FaHospitalAlt} from './assets/icons/FaHospitalAlt.svg'
import {ReactComponent as FaUserMd} from './assets/icons/FaUserMd.svg'
import {ReactComponent as FaUsers} from './assets/icons/FaUsers.svg'
import {ReactComponent as GiMedallist} from './assets/icons/GiMedallist.svg'
import {ReactComponent as IoChatBubbles} from './assets/icons/IoChatBubbles.svg'
import {ReactComponent as IoHome} from './assets/icons/IoHome.svg'
import {ReactComponent as MdSick} from './assets/icons/MdSick.svg'
import {ReactComponent as MdSupportAgent} from './assets/icons/MdSupportAgent.svg'
import {ReactComponent as SettingIcon} from './assets/icons/SettingIcon.svg'

import {
    getCollapsedAppMenu,
    getMobileAppMenuActive,
    setCollapsedAppMenu,
    setMobileAppMenuActive,
} from './AppUtils'
import {
    clearStorage,
    getClientId,
    getSuperAdminRole,
} from './components/authentication/AuthUtils'
import Authentication from './components/authentication/Authentication'
import Room from './components/chat/Room'
import ClientDetailPage from './components/client/ClientDetailPage'
import ClientDataTable from './components/client/ClientTable'
import StyledConfiguration from './components/configuration/StyledConfiguration'
import DnDLayout from './components/dnd/DnDLayout'
import CoolDown from './components/error/CoolDown'
import PageError from './components/error/PageError'
import HealthCarePractitionerDetail from './components/healthcare/HealthCarePractitionerDetail'
import Profile from './components/information/Profile'
import NotificationProvider from './components/notification/NotificationProvider'
import {onMenuChanged} from './components/observations/ObservationService'
import OverlayTooltip from './components/overlay-tooltip/OverlayTooltip'
import PatientDashboard from './components/patient-page/PatientDashboard'
import ReportQueryBuilder from './components/reporting/ReportQueryBuilder'
import StyledGeneralPractitionerDataTable from './components/styled-component/general-practitioner/GeneralPractitionerDataTable'
import StyledHealthCarePractitionerDatatable from './components/styled-component/hc-practitioner/HealthCarePractitionerDatatable'
import StyledHospitalDatatable from './components/styled-component/hospital/HospitalDatatable'
import StyleDetailInformationWrapper from './components/styled-component/information/StyleDetailInformationWrapper'
import StyledPathwayDatatable from './components/styled-component/pathway/PathwayDatatable'
import StyledPatientDatatable from './components/styled-component/patient/PatientDatatable'
import StyledReservationDataTable from './components/styled-component/reservations/ReservationsDataTable'
import StyledSupportTeamDataTable from './components/styled-component/support-team/SupportTeamDataTable'
import {ImportNotificationProvider} from './components/table-utils/importForm/ImportNotificationProvider'
import TermsAndCondition from './components/terms-and-condition/TermsAndCondition'
import {useAppConfig} from './context/app/AppContext'
import TranslateUtils from './i18n/TranslateUtils'
import {openPage} from './utils/NavigatorUtils'
//#endregion

const App = () => {
    useSignals()
    const {userRole} = useAppConfig()
    const [layoutMode, setLayoutMode] = useState('static')
    // eslint-disable-next-line no-unused-vars
    const [layoutColorMode, setLayoutColorMode] = useState('light')
    // eslint-disable-next-line no-unused-vars
    const [inputStyle, setInputStyle] = useState('outlined')
    // eslint-disable-next-line no-unused-vars
    const [ripple, setRipple] = useState(true)
    const [staticMenuInactive, setStaticMenuInactive] = useState(
        !!getSuperAdminRole() || userRole === SystemRole.USER,
    )
    const [overlayMenuActive, setOverlayMenuActive] = useState(false)
    const [mobileMenuActive, setMobileMenuActive] = useState(
        getMobileAppMenuActive(),
    )
    const [mobileTopbarMenuActive, setMobileTopbarMenuActive] = useState(false)
    const [isCollapsed, setIsCollapsed] = useState(getCollapsedAppMenu())

    const copyTooltipRef = useRef()
    const location = useLocation()
    const {translateCommons} = TranslateUtils()
    PrimeReact.ripple = true

    let menuClick = false
    let mobileTopbarMenuClick = false

    const onIdle = () => {
        clearStorage()
        openPage('/')
    }

    const {getRemainingTime} = useIdleTimer({
        onIdle,
        timeout: 30 * 60 * 1000,
        throttle: 500,
    })

    useEffect(() => {
        const interval = setInterval(() => {
            if (getRemainingTime() / 1000 === 0) {
                clearStorage()
                openPage('/')
            }
        }, 1000)

        return () => {
            clearInterval(interval)
        }
    })

    useEffect(() => {
        document.documentElement.style.fontSize = '13.5px'
        if (mobileMenuActive) {
            addClass(document.body, 'body-overflow-hidden')
        } else {
            removeClass(document.body, 'body-overflow-hidden')
        }
    }, [mobileMenuActive])

    const isDesktop = useCallback(() => {
        return window.innerWidth >= 1200
    }, [])

    useEffect(() => {
        if (isDesktop()) {
            if (
                (!!getSuperAdminRole() &&
                    !getClientId() &&
                    location.pathname === '/') ||
                userRole === SystemRole.USER ||
                location.pathname.includes('/information') ||
                location.pathname.includes('/clients') ||
                location.pathname === '/404'
            ) {
                setLayoutMode('overlay')
                setStaticMenuInactive(false)
            } else if (location.pathname.includes) {
                setLayoutMode('static')
                setStaticMenuInactive(false)
            }
        } else {
            if (
                !!getSuperAdminRole() ||
                userRole === SystemRole.USER ||
                location.pathname.includes('/patient') ||
                location.pathname.includes('/information') ||
                location.pathname.includes('/hcp') ||
                location.pathname.includes('?tv=')
            ) {
                setMobileMenuActive(false)
                setMobileAppMenuActive(false)
            } else {
                // setMobileMenuActive(prevState => !prevState)
            }
        }

        copyTooltipRef &&
            copyTooltipRef.current &&
            copyTooltipRef.current.updateTargetEvents()
    }, [location, isDesktop, copyTooltipRef, userRole])

    const onWrapperClick = event => {
        if (!menuClick) {
            setOverlayMenuActive(false)
            setMobileMenuActive(false)
            setMobileAppMenuActive(false)
        }

        if (!mobileTopbarMenuClick) {
            setMobileTopbarMenuActive(false)
        }

        mobileTopbarMenuClick = false
        menuClick = false
    }

    const onToggleMenuClick = event => {
        menuClick = true

        onMenuChanged()

        if (isDesktop()) {
            if (layoutMode === 'overlay') {
                if (mobileMenuActive === true) {
                    setOverlayMenuActive(true)
                }

                setOverlayMenuActive(prevState => !prevState)
                setMobileMenuActive(false)
                setMobileAppMenuActive(false)
            } else if (layoutMode === 'static') {
                setStaticMenuInactive(prevState => !prevState)
            }
        } else {
            let value = !mobileMenuActive
            setMobileMenuActive(value)
            setMobileAppMenuActive(value)
        }

        event?.preventDefault()
    }

    const onSidebarClick = useCallback(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        menuClick = true
    }, [])

    const onMobileTopbarMenuClick = event => {
        mobileTopbarMenuClick = true

        setMobileTopbarMenuActive(prevState => !prevState)
        event.preventDefault()
    }

    const onMobileSubTopbarMenuClick = event => {
        mobileTopbarMenuClick = true

        event.preventDefault()
    }

    const onMenuItemClick = useCallback(event => {
        if (!event.item.items) {
            setOverlayMenuActive(false)
            setMobileMenuActive(false)
            setMobileAppMenuActive(false)
        }
    }, [])

    const onMenuCollapsed = e => {
        let value = !isCollapsed
        setIsCollapsed(value)
        setCollapsedAppMenu(value)
    }

    //#region Menu

    const menu = useMemo(() => {
        if (!userRole) return []
        const iconStyled = {className: 'app-menu-icon'}

        return [
            {
                label: '',
                items: [
                    {
                        label: translateCommons('Dashboard'),
                        iconComp: () => <IoHome {...iconStyled} />,
                        to: '/',
                        hidden: !!getSuperAdminRole(),
                    },
                    {
                        label: translateCommons('Hospitals'),
                        iconComp: () => <FaHospitalAlt {...iconStyled} />,
                        to: '/hospitals',
                        hidden: userRole !== SystemRole.ADMIN,
                    },
                    {
                        label: translateCommons('Medical Staff Members'),
                        iconComp: () => <FaUserMd {...iconStyled} />,
                        to: '/hcp',
                        hidden: !!getSuperAdminRole(),
                    },

                    {
                        label: translateCommons('Patients'),
                        iconComp: () => <FaUsers {...iconStyled} />,
                        to: '/patients',
                        hidden: !!getSuperAdminRole(),
                    },
                    {
                        label: translateCommons('Reservations'),
                        iconComp: () => <CalendarIcon {...iconStyled} />,
                        to: '/reservations',

                        hidden:
                            !!getSuperAdminRole() ||
                            userRole === SystemRole.ADMIN,
                    },
                    {
                        label: translateCommons('Pathways '),
                        iconComp: () => <MdSick {...iconStyled} />,
                        to: '/pathway',
                    },
                    {
                        label: translateCommons('General Practitioner'),
                        iconComp: () => <GiMedallist {...iconStyled} />,
                        to: '/generalPractitioner',
                    },
                    {
                        label: translateCommons('Support Teams'),
                        iconComp: () => <MdSupportAgent {...iconStyled} />,
                        to: '/support-teams',
                        hidden: !!getSuperAdminRole(),
                    },
                    // {
                    //     label: translateCommons('Report'),
                    //     iconComp: () => <MdSupportAgent {...iconStyled} />,
                    //     to: '/report',
                    //     hidden: !!getSuperAdminRole(),
                    // },
                    {
                        label: translateCommons('Conversation'),
                        iconComp: () => <IoChatBubbles {...iconStyled} />,
                        to: '/conversations',
                        hidden: !!getSuperAdminRole(),
                    },
                    {
                        label: translateCommons('Configuration'),
                        iconComp: () => <SettingIcon {...iconStyled} />,
                        to: '/configuration',
                        hidden: !!getSuperAdminRole(),
                    },
                ],
            },
        ]
    }, [translateCommons, userRole])
    //#endregion

    //#region Routing
    const routing = useMemo(() => {
        if (
            !!getSuperAdminRole() &&
            (location.pathname.includes('/clients') || !getClientId())
        ) {
            return (
                <Switch>
                    <Route
                        path="/login"
                        render={() => <Authentication type={'login'} />}
                    />
                    <Route
                        path="/forget-password"
                        render={() => (
                            <Authentication type={'forgetPassword'} />
                        )}
                    />
                    <Route path="/information" component={Profile} />
                    <Route path="/429" component={CoolDown} />
                    <Route path="/404" exact component={PageError} />
                    <Route path="/clients/:id" component={ClientDetailPage} />
                    <Route path="/" exact component={ClientDataTable} />
                    <Route path="*" component={PageError} />
                </Switch>
            )
        } else if (!!getSuperAdminRole() && userRole === SystemRole.ADMIN) {
            return (
                <Switch>
                    <Route
                        path="/login"
                        render={() => <Authentication type={'login'} />}
                    />
                    <Route
                        path="/forget-password"
                        render={() => (
                            <Authentication type={'forgetPassword'} />
                        )}
                    />
                    <Route path="/information" component={Profile} />
                    <Route path="/429" component={CoolDown} />
                    <Route path="/404" exact component={PageError} />
                    <Route
                        path="/hospitals"
                        component={StyledHospitalDatatable}
                    />
                    <Route
                        path="/generalPractitioner"
                        component={StyledGeneralPractitionerDataTable}
                    />
                    <Route path="/pathway" component={StyledPathwayDatatable} />
                    <Route exact path="/" component={StyledHospitalDatatable} />
                    <Route path="*" component={PageError} />
                </Switch>
            )
        }

        if (!getSuperAdminRole()) {
            switch (userRole) {
                case SystemRole.ADMIN:
                    return (
                        <Switch>
                            <Route
                                path="/hospitals"
                                component={StyledHospitalDatatable}
                            />
                            <Route
                                path="/patients/:id"
                                component={StyleDetailInformationWrapper}
                            />
                            <Route
                                path="/patients"
                                component={StyledPatientDatatable}
                            />
                            <Route
                                path="/pathway"
                                component={StyledPathwayDatatable}
                            />
                            <Route
                                path="/generalPractitioner"
                                component={StyledGeneralPractitionerDataTable}
                            />
                            <Route
                                path="/report"
                                component={ReportQueryBuilder}
                            />
                            <Route
                                path="/hcp/:id"
                                component={HealthCarePractitionerDetail}
                            />
                            <Route
                                path="/hcp"
                                component={
                                    StyledHealthCarePractitionerDatatable
                                }
                            />
                            <Route path="/information" component={Profile} />
                            <Route path="/conversations" component={Room} />
                            <Route
                                path="/configuration"
                                component={StyledConfiguration}
                            />
                            <Route
                                path="/terms"
                                component={TermsAndCondition}
                            />
                            <Route
                                path="/support-teams"
                                component={StyledSupportTeamDataTable}
                            />
                            <Route path="/429" component={CoolDown} />
                            <Route path="/404" exact component={PageError} />
                            <Route
                                path="/login"
                                render={() => <Authentication type={'login'} />}
                            />
                            <Route
                                path="/forget-password"
                                render={() => (
                                    <Authentication type={'forgetPassword'} />
                                )}
                            />
                            <Route exact path="/" component={DnDLayout} />
                            <Route path="*" component={PageError} />
                        </Switch>
                    )
                case SystemRole.USER_WITH_ROLE:
                    return (
                        <Switch>
                            <Route
                                path="/patients/:id"
                                component={StyleDetailInformationWrapper}
                            />
                            <Route
                                path="/patients"
                                component={StyledPatientDatatable}
                            />
                            <Route
                                path="/pathway"
                                component={StyledPathwayDatatable}
                            />
                            <Route
                                path="/hcp/:id"
                                component={HealthCarePractitionerDetail}
                            />
                            <Route
                                path="/hcp"
                                component={
                                    StyledHealthCarePractitionerDatatable
                                }
                            />
                            <Route
                                path="/reservations"
                                component={StyledReservationDataTable}
                            />
                            <Route path="/information" component={Profile} />
                            <Route
                                path="/terms"
                                component={TermsAndCondition}
                            />
                            <Route
                                path="/report"
                                component={ReportQueryBuilder}
                            />
                            <Route
                                path="/generalPractitioner"
                                component={StyledGeneralPractitionerDataTable}
                            />
                            <Route
                                path="/support-teams"
                                component={StyledSupportTeamDataTable}
                            />
                            <Route path="/conversations" component={Room} />
                            <Route
                                path="/configuration"
                                component={StyledConfiguration}
                            />
                            <Route path="/429" component={CoolDown} />
                            <Route path="/404" exact component={PageError} />

                            <Route
                                path="/login"
                                render={() => <Authentication type={'login'} />}
                            />
                            <Route
                                path="/forget-password"
                                render={() => (
                                    <Authentication type={'forgetPassword'} />
                                )}
                            />

                            <Route exact path="/" component={DnDLayout} />

                            <Route path="*" component={PageError} />
                        </Switch>
                    )
                case SystemRole.USER:
                    return (
                        <Switch>
                            <Route
                                path="/information"
                                render={() => <Profile />}
                            />
                            <Route
                                path="/login"
                                render={() => <Authentication type={'login'} />}
                            />
                            <Route
                                path="/forget-password"
                                render={() => (
                                    <Authentication type={'forgetPassword'} />
                                )}
                            />
                            <Route path="/429" component={CoolDown} />
                            <Route path="/404" exact component={PageError} />

                            <Route
                                exact
                                path="/"
                                component={PatientDashboard}
                            />
                            <Route path="*" render={() => <PageError />} />
                        </Switch>
                    )
                default:
                    return
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userRole, location.pathname])
    //#endregion

    //#region Layout
    const addClass = (element, className) => {
        if (element.classList) element.classList.add(className)
        else element.className += ' ' + className
    }

    const removeClass = (element, className) => {
        if (element.classList) element.classList.remove(className)
        else
            element.className = element.className.replace(
                new RegExp(
                    '(^|\\b)' + className.split(' ').join('|') + '(\\b|$)',
                    'gi',
                ),
                ' ',
            )
    }

    const wrapperClass = classNames('layout-wrapper', {
        'layout-overlay': layoutMode === 'overlay',
        'layout-static': layoutMode === 'static',
        'layout-static-sidebar-inactive':
            staticMenuInactive && layoutMode === 'static',
        'layout-overlay-sidebar-active':
            overlayMenuActive && layoutMode === 'overlay',
        'layout-mobile-sidebar-active': mobileMenuActive,
        'p-input-filled': inputStyle === 'filled',
        'p-ripple-disabled': ripple === false,
        'layout-theme-custom': layoutColorMode === 'light',
        'layout-theme-dark': layoutColorMode !== 'light',
    })
    //#endregion

    const isPatientPage = () => {
        let currentLocation = window.location.pathname
        if (
            currentLocation !== '/' &&
            currentLocation !== '/patients' &&
            currentLocation.includes('/patients')
        ) {
            return true
        }
        return false
    }

    return (
        <div id="main-app" className={wrapperClass} onClick={onWrapperClick}>
            <Tooltip
                ref={copyTooltipRef}
                target=".block-action-copy"
                position="bottom"
                content={translateCommons('Copied to clipboard')}
                event="focus"
            />

            <NotificationProvider>
                <ImportNotificationProvider>
                    <React.Fragment>
                        <div id="layout-main-container-wrapper">
                            <AppTopbar
                                onToggleMenuClick={onToggleMenuClick}
                                layoutColorMode={layoutColorMode}
                                mobileTopbarMenuActive={mobileTopbarMenuActive}
                                onMobileTopbarMenuClick={
                                    onMobileTopbarMenuClick
                                }
                                isCollapsed={
                                    !mobileMenuActive ? isCollapsed : false
                                }
                                mobileMenuActive={mobileMenuActive}
                                onMobileSubTopbarMenuClick={
                                    onMobileSubTopbarMenuClick
                                }
                                isActiveCollapseMenu={
                                    !!staticMenuInactive && !overlayMenuActive
                                }
                            />
                            <div
                                className={`layout-sidebar layout-sidebar-custom  ${
                                    !mobileMenuActive && isCollapsed
                                        ? `collapsed-sidebar `
                                        : ''
                                }  m-0 p-0`}
                                onClick={onSidebarClick}>
                                <AppMenu
                                    model={menu}
                                    isCollapsed={
                                        !mobileMenuActive ? isCollapsed : false
                                    }
                                    mobileMenuActive={mobileMenuActive}
                                    onMenuItemClick={onMenuItemClick}
                                    layoutColorMode={layoutColorMode}
                                    onMenuCollapsed={onMenuCollapsed}
                                />
                            </div>
                            <div
                                className={`layout-main-container ${
                                    isCollapsed &&
                                    'collapsed-layout-main-container'
                                } ${staticMenuInactive && 'not-margin'}`}>
                                <div
                                    className={`layout-main ${
                                        !isPatientPage() && 'p-2'
                                    }`}>
                                    {routing}
                                </div>

                                {/* <AppFooter layoutColorMode={layoutColorMode} /> */}
                            </div>
                        </div>
                    </React.Fragment>
                </ImportNotificationProvider>
            </NotificationProvider>
            <OverlayTooltip />
            <CSSTransition
                classNames="layout-mask"
                timeout={{
                    enter: 200,
                    exit: 200,
                }}
                in={mobileMenuActive}
                unmountOnExit>
                <div className="layout-mask p-component-overlay"></div>
            </CSSTransition>
        </div>
    )
}

export default App
